import {assertPartialSchema, createAction, t, useSelector, v} from '../../../lib'
import {Adherence, AdherenceGarminDirect, DigestDay, ScheduleTaskState} from '../../../model/adherence'
import {GarminDeviceLogDataType, TimeSlotWithTaskRecord} from '../../../shared/mongo'

import {
  hydrateProjectDataDigest,
  MultiDayTimeSlotMap,
  ParticipantDataDigest,
  ProjectDataDigest,
  SerializableProjectDataDigest,
} from '../project_data_digest'

export enum AdherenceActionType {
  ADHERENCE_SET = 'ADHERENCE_SET',
  ADHERENCE_UPDATE = 'ADHERENCE_UPDATE',
  ADHERENCE_DELETE = 'ADHERENCE_DELETE',
  ADHERENCE_PROJECT_PARTICIPANT_MAP_SET = 'ADHERENCE_PROJECT_PARTICIPANT_MAP_SET',
}

export const doADHERENCE_SET = createAction(AdherenceActionType.ADHERENCE_SET)
export const doADHERENCE_DELETE = createAction(AdherenceActionType.ADHERENCE_DELETE)
export const doADHERENCE_PROJECT_PARTICIPANT_MAP_SET = createAction(
  AdherenceActionType.ADHERENCE_PROJECT_PARTICIPANT_MAP_SET,
)
export const doADHERENCE_UPDATE = createAction(AdherenceActionType.ADHERENCE_UPDATE)

interface AdherenceState {
  [projectId: string]: ProjectAdherence
}

export interface ParticipantAdherence {
  [participantId: string]: Adherence
}

export interface ProjectAdherence {
  participantAdherence?: ParticipantAdherence
  garminTaskId?: string
  lastDigestDayDetailUpdatedTime?: number
}

type GarminAdherenceKey = keyof AdherenceGarminDirect

interface RootState {
  adherence: AdherenceState
}

/* selector */
export const selectAdherenceData = () => {
  return useSelector((state: RootState) => state.adherence)
}

export const selectProjectAdherenceData = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId) {
      return state.adherence[projectId]
    } else return {}
  })
}

export const selectParticipantAdherence = (projectId: string, participantId: string) => {
  return useSelector((state: RootState) => {
    return state.adherence[projectId]?.participantAdherence?.[participantId]
  })
}

export const selectParticipantAdherenceDigestDayMap = (projectId: string, participantId: string) => {
  return useSelector((state: RootState) => {
    return state.adherence[projectId]?.participantAdherence?.[participantId]?.digestDayMap
  })
}

export const selectParticipantAdherenceDigest = (projectId: string, participantId: string, yymmdd: string) => {
  return useSelector((state: RootState) => {
    return state.adherence[projectId]?.participantAdherence?.[participantId]?.digestDayMap?.[yymmdd]
  })
}

export const adherenceActionCreators = {
  doADHERENCE_SET,
  doADHERENCE_UPDATE,
  doADHERENCE_DELETE,
  doADHERENCE_PROJECT_PARTICIPANT_MAP_SET,
}

export const adherenceDefaultState: AdherenceState = {}

type Action =
  | {
      type: AdherenceActionType.ADHERENCE_SET
      payload: {
        projectId: string
        batchId: string
        startDate: Date | string
        endDate: Date | string
        batchAdherenceList: Adherence[]
      }
    }
  | {
      type: AdherenceActionType.ADHERENCE_DELETE
      payload: {
        projectId: string
      }
    }
  | {
      type: AdherenceActionType.ADHERENCE_PROJECT_PARTICIPANT_MAP_SET
      payload: {
        projectId: string
        projectParticipantList: Adherence[]
      }
    }
  | {
      type: AdherenceActionType.ADHERENCE_UPDATE
      payload: {
        garminTaskId: string | undefined
        projectDataDigest: SerializableProjectDataDigest | undefined
        inputProjectDataDigest: ProjectDataDigest
      }
    }

export const adherenceReducer = (state = {...adherenceDefaultState}, {type, payload}: Action): AdherenceState => {
  switch (type) {
    case AdherenceActionType.ADHERENCE_UPDATE:
      assertPartialSchema({
        payload,
        schema: v.object({
          garminTaskId: v.string().optional(),
          projectDataDigest: v.object().optional(),
          inputProjectDataDigest: v.object({
            projectId: v.string().exist(),
            coveredYYMMIndex: v.array().items(v.number()).exist(),
            participantDataDigestMap: v.object().exist(),
          }),
        }),
      })

      return updateAdherence(state, payload.inputProjectDataDigest, payload.projectDataDigest, payload.garminTaskId)

    case AdherenceActionType.ADHERENCE_PROJECT_PARTICIPANT_MAP_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          projectId: v.string().uuid().exist(),
          projectParticipantList: v.array(),
        }),
      })

      if (payload?.projectParticipantList) {
        const projectId = payload.projectId
        const existingProjectAdherence = state[projectId] ?? {}
        const participantAdherence: ParticipantAdherence = {...(existingProjectAdherence.participantAdherence ?? {})}

        const adherenceList = payload?.projectParticipantList
        if (adherenceList) {
          for (const adherence of adherenceList) {
            const participantId = adherence.participantId
            const digestDataMap: {[yymmdd: string]: DigestDay} = {
              ...(state[projectId]?.participantAdherence?.[participantId]?.digestDayMap ?? {}),
            }
            for (const digestDay of adherence.digestDayList) {
              digestDataMap[digestDay.yymmddIndex] = {
                ...state[projectId]?.participantAdherence?.[participantId]?.digestDayMap?.[digestDay.yymmddIndex],
                ...digestDay,
              }
            }
            participantAdherence[participantId] = {
              ...adherence,
              digestDayMap: digestDataMap,
            }
          }
          // delete digestList
        }

        return {
          ...state,
          [projectId]: {
            ...existingProjectAdherence,
            participantAdherence,
          },
        }
      }
      return state
    }

    case AdherenceActionType.ADHERENCE_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          projectId: v.string().uuid().exist(),
          batchId: v.string().uuid().exist(),
          startDate: v.date(),
          endDate: v.date(),
          batchAdherenceList: v.array(),
        }),
      })
      if (payload?.batchAdherenceList) {
        const projectId = payload.projectId
        const existingProjectAdherence = state[projectId] ?? {}
        const participantAdherence: ParticipantAdherence = {}

        const adherenceList = payload?.batchAdherenceList
        if (adherenceList) {
          for (const adherence of adherenceList) {
            const participantId = adherence.participantId
            const digestDataMap: {[yymmdd: string]: DigestDay} = {}
            for (const digestDay of adherence.digestDayList) {
              digestDataMap[digestDay.yymmddIndex] = digestDay
            }
            participantAdherence[participantId] = {
              ...adherence,
              digestDayMap: digestDataMap,
            }
          }
          // delete digestList
        }

        return {
          ...state,
          [projectId]: {
            ...existingProjectAdherence,
            participantAdherence,
          },
        }
      }
      return state
    }

    case AdherenceActionType.ADHERENCE_DELETE: {
      assertPartialSchema({
        payload,
        schema: v.object({
          projectId: v.string().uuid().exist(),
        }),
      })
      const newState = {...state}
      delete newState[payload.projectId]
      return newState
    }

    default:
      return state
  }
}

function updateAdherence(
  state: AdherenceState,
  inputProjectDataDigest: ProjectDataDigest,
  projectDataDigest?: SerializableProjectDataDigest,
  garminTaskId?: string,
) {
  const projectId = inputProjectDataDigest.projectId
  const existingProjectAdherence = state[projectId]
  const participantAdherence = existingProjectAdherence?.participantAdherence
  const hydratedProjectDataDigest = projectDataDigest ? hydrateProjectDataDigest(projectDataDigest) : undefined
  if (participantAdherence && hydratedProjectDataDigest && existingProjectAdherence) {
    const adherenceList = Object.values(participantAdherence)
    const updatedParticipantAdherence = {...participantAdherence}
    const updatedAdherenceList = updateAdherenceList(
      adherenceList,
      hydratedProjectDataDigest,
      inputProjectDataDigest,
      garminTaskId,
    )

    updatedAdherenceList.forEach((adherence) => {
      const participantId = adherence.participantId
      updatedParticipantAdherence[participantId] = adherence
    })

    return {
      ...state,
      [projectId]: {
        ...existingProjectAdherence,
        participantAdherence: updatedParticipantAdherence,
      },
    }
  }
  return state
}

function updateAdherenceList(
  adherenceList: Adherence[],
  projectDataDigest: ProjectDataDigest,
  inputProjectDataDigest: ProjectDataDigest,
  garminTaskId?: string,
): Adherence[] {
  const updateDigestDayMapOnTask = (
    digestDayMap: Record<number, DigestDay>,
    yymmddList: number[],
    taskId: string,
    yymmIndexesToDigest?: number[],
    taskType?: string,
  ) => {
    yymmddList.forEach((yymmdd) => {
      if (yymmIndexesToDigest) {
        const yymmIndex = Math.trunc(yymmdd / 100)
        if (!yymmIndexesToDigest.includes(yymmIndex)) return
      }

      const taskData = {
        taskType: taskType,
      }
      if (digestDayMap[yymmdd]) {
        if (digestDayMap[yymmdd].task) {
          digestDayMap[yymmdd].task = {...digestDayMap[yymmdd].task, [taskId]: taskData}
        } else {
          digestDayMap[yymmdd].task = {[taskId]: taskData}
        }
      } else {
        const newDigestDay = getEmptyDigestDay(yymmdd)
        newDigestDay.task = {[taskId]: taskData}
        digestDayMap[yymmdd] = newDigestDay
      }
    })

    if (taskType === 'garmin_device') {
      yymmddList.forEach((yymmdd) => {
        digestDayMap[yymmdd].garminDirectCollected = true
      })
    }

    return digestDayMap
  }

  const updateDigestDayMapOnTaskCompletionCountMap = (
    yymmIndexesToDigest: number[],
    digestDayMap: Record<number, DigestDay>,
    taskId: string,
    dateCompletionCountMap: Record<string, number>,
  ) => {
    for (const [yymmddString, completionCount] of Object.entries(dateCompletionCountMap)) {
      const yymmIndex = Number(yymmddString.slice(0, 4))
      if (!yymmIndexesToDigest.includes(yymmIndex)) continue

      const yymmdd = +yymmddString
      const existingDigestDay = digestDayMap[yymmdd]
      const taskCompletionCountMap = existingDigestDay?.taskCompletionCountMap ?? {}

      digestDayMap[yymmdd] = {
        ...(existingDigestDay ?? getEmptyDigestDay(yymmdd)),
        taskCompletionCountMap: {
          ...taskCompletionCountMap,
          [taskId]: completionCount,
        },
      }
    }

    return digestDayMap
  }

  const updateDigestDayMapOnScheduleTaskStateMap = (
    newYymmCoverdIndexes: number[],
    digestDayMap: Record<number, DigestDay>,
    participantDigest: ParticipantDataDigest,
  ) => {
    if (participantDigest.taskTimeSlotCombination) {
      for (const [taskId, timeSlotCombination] of Object.entries(participantDigest.taskTimeSlotCombination)) {
        if (timeSlotCombination?.yymmddTimeSlotMap) {
          digestDayMap = updateDigestDayMapOnTimeSlotList(
            newYymmCoverdIndexes,
            digestDayMap,
            taskId,
            timeSlotCombination.yymmddTimeSlotMap,
          )
        }
      }
    }

    if (participantDigest.taskMultiDayTimeSlotMap) {
      for (const [taskId, multiDayTimeSlotMap] of Object.entries(participantDigest.taskMultiDayTimeSlotMap)) {
        if (multiDayTimeSlotMap) {
          digestDayMap = updateDigestDayMapOnMultiDayTimeSlotIdList(
            newYymmCoverdIndexes,
            digestDayMap,
            taskId,
            multiDayTimeSlotMap,
          )
        }
      }
    }

    return digestDayMap
  }

  const updateDigestDayMapOnTimeSlotList = (
    yymmIndexesToDigest: number[],
    digestDayMap: Record<number, DigestDay>,
    taskId: string,
    yymmddTimeSlotMap: Record<string, TimeSlotWithTaskRecord[]>,
  ): Record<number, DigestDay> => {
    for (const [yymmddString, timeSlotList] of Object.entries(yymmddTimeSlotMap)) {
      const yymmIndex = Number(yymmddString.slice(0, 4))

      if (!yymmIndexesToDigest.includes(yymmIndex)) continue

      //filter duplicated time slots
      let filteredTimeSlotList: TimeSlotWithTaskRecord[] = []
      if (timeSlotList.length > 1) {
        const timeSlotSetMap: Record<string, TimeSlotWithTaskRecord> = {}
        timeSlotList.forEach((slot) => {
          timeSlotSetMap[slot.id] = slot
        })
        filteredTimeSlotList = Object.values(timeSlotSetMap)
      } else {
        filteredTimeSlotList = timeSlotList
      }
      const yymmdd = Number(yymmddString)
      const existingDigestDay = digestDayMap[yymmdd] ?? getEmptyDigestDay(yymmdd)
      const scheduleTaskStateMap = existingDigestDay.scheduleTaskStateMap ?? {}
      const completedCount = filteredTimeSlotList.reduce((count, slot) => (slot.taskRecord ? count + 1 : count), 0)

      const scheduleTaskState: ScheduleTaskState = {
        display: true,
        timeSlotList: filteredTimeSlotList,
        completedCount,
        totalCount: filteredTimeSlotList.length,
      }

      digestDayMap[yymmdd] = {
        ...existingDigestDay,
        scheduleTaskStateMap: {
          ...scheduleTaskStateMap,
          [taskId]: scheduleTaskState,
        },
      }
    }

    return digestDayMap
  }

  const updateDigestDayMapOnMultiDayTimeSlotIdList = (
    yymmIndex: number[],
    digestDayMap: Record<number, DigestDay>,
    taskId: string,
    multiDayTimeSlotMap: MultiDayTimeSlotMap,
  ) => {
    const sortedYymmIndexes = [...yymmIndex].sort((a, b) => a - b)

    const minYymmdd = sortedYymmIndexes[0] * 100 + 1
    const maxYymmdd = t.getLastYymmddOfYymm(sortedYymmIndexes[sortedYymmIndexes.length - 1])

    // for (const multiDayTimeSlot of multiDayTimeSlotMap) {
    multiDayTimeSlotMap.forEach((multiDayTimeSlot) => {
      if (multiDayTimeSlot.yymmddStart > maxYymmdd || multiDayTimeSlot.yymmddEnd < minYymmdd) {
        return
      }

      const yymmddToInsertStart = Math.max(multiDayTimeSlot.yymmddStart, minYymmdd)
      let yymmddToInsertEnd = Math.min(multiDayTimeSlot.yymmddEnd, maxYymmdd)

      // set end to complete date
      let hasCompleted = false
      if (multiDayTimeSlot.yymmddComplete && multiDayTimeSlot.yymmddComplete < yymmddToInsertEnd) {
        yymmddToInsertEnd = multiDayTimeSlot.yymmddComplete
        hasCompleted = true
      }

      const yymmddToInsert = t.toYYMMDDListFromRange(yymmddToInsertStart, yymmddToInsertEnd)

      for (const yymmdd of yymmddToInsert) {
        const existingDigestDay = digestDayMap[yymmdd] ?? getEmptyDigestDay(yymmdd)
        const scheduleTaskStateMap = existingDigestDay?.scheduleTaskStateMap ?? {}
        const scheduleTaskState: ScheduleTaskState = scheduleTaskStateMap[taskId] ?? {}

        // update multiDayTimeSlotList
        scheduleTaskState.multiDayTimeSlotIdList ??= []
        scheduleTaskState.multiDayTimeSlotIdList.push(multiDayTimeSlot.id)

        scheduleTaskState.totalCount = (scheduleTaskState.totalCount ?? 0) + 1
        // update complete record & count
        if (hasCompleted) {
          if (yymmdd === multiDayTimeSlot.yymmddComplete) {
            scheduleTaskState.multiDayTaskRecords ??= []
            if (multiDayTimeSlot.taskRecord) {
              scheduleTaskState.multiDayTaskRecords.push(multiDayTimeSlot.taskRecord)
            }

            // update total count and complete count only when it completed on the date
            // scheduleTaskState.totalCount = (scheduleTaskState.totalCount ?? 0) + 1
            scheduleTaskState.completedCount = (scheduleTaskState.completedCount ?? 0) + 1

            // display when it completed on the date
            scheduleTaskState.display = true
          }
        } else if (yymmdd === multiDayTimeSlot.yymmddEnd) {
          if (scheduleTaskState.completedCount === undefined) {
            scheduleTaskState.completedCount = 0
          }
          // display when it expired on the date
          scheduleTaskState.display = true
        }

        digestDayMap[yymmdd] = {
          ...existingDigestDay,
          scheduleTaskStateMap: {
            ...scheduleTaskStateMap,
            [taskId]: scheduleTaskState,
          },
        }
      }
    })

    return digestDayMap
  }

  const newYymmCoverdIndexes = inputProjectDataDigest.coveredYYMMIndex

  return adherenceList.map((adherence) => {
    const participantId = adherence.participantId
    let digestDayMap: Record<number, DigestDay> = {}

    // convert task data related digest from projectDataDigest (data in state)
    // because it will be fetched one time and get all records
    // convert only needed yymmIndex range because the old ones is not needed for adherence view yet
    const participantDigest = projectDataDigest.participantDataDigestMap[participantId]
    if (participantDigest) {
      if (participantDigest.taskDataDates) {
        for (const [taskId, yymmddList] of Object.entries(participantDigest.taskDataDates)) {
          digestDayMap = updateDigestDayMapOnTask(digestDayMap, yymmddList, taskId, newYymmCoverdIndexes)
        }
      }

      if (participantDigest.taskCompletionDateCountMap) {
        for (const [taskId, dateCompletionCountMap] of Object.entries(participantDigest.taskCompletionDateCountMap)) {
          if (dateCompletionCountMap) {
            digestDayMap = updateDigestDayMapOnTaskCompletionCountMap(
              newYymmCoverdIndexes,
              digestDayMap,
              taskId,
              dateCompletionCountMap,
            )
          }
        }
      }

      if (participantDigest) {
        digestDayMap = updateDigestDayMapOnScheduleTaskStateMap(newYymmCoverdIndexes, digestDayMap, participantDigest)
      }
    }

    // convert device task data digest from inputProjectDataDigest (data from API)
    // it's new data need to be transformed into digest
    const newParticipantDigest = inputProjectDataDigest.participantDataDigestMap[participantId]
    if (newParticipantDigest) {
      if (garminTaskId && newParticipantDigest.garminDeviceDataDates) {
        digestDayMap = updateDigestDayMapOnTask(
          digestDayMap,
          newParticipantDigest.garminDeviceDataDates,
          garminTaskId,
          undefined,
          'garmin_device',
        )
      }

      if (newParticipantDigest.garminDeviceDataDatesByType) {
        for (const [dataType, dates] of Object.entries(newParticipantDigest.garminDeviceDataDatesByType)) {
          const adherenceKey = convertGarminDataTypeToAdherenceKey(dataType)

          if (adherenceKey) {
            dates.forEach((yymmdd) => {
              if (digestDayMap[yymmdd]) {
                const garminDirectAdherence = digestDayMap[yymmdd].garminDirectAdherence
                if (garminDirectAdherence) {
                  garminDirectAdherence[adherenceKey] = true
                } else {
                  digestDayMap[yymmdd].garminDirectAdherence = {
                    [adherenceKey]: true,
                  }
                }
              } else {
                const newDigestDay = getEmptyDigestDay(yymmdd)
                newDigestDay.garminDirectAdherence = {
                  [adherenceKey]: true,
                }
                digestDayMap[yymmdd] = newDigestDay
              }
            })
          }
        }
      }

      if (newParticipantDigest.garminConnectDataDates) {
        newParticipantDigest.garminConnectDataDates.forEach((yymmdd) => {
          if (digestDayMap[yymmdd]) {
            digestDayMap[yymmdd].garminConnectSynced = true
          } else {
            const newDigestDay = getEmptyDigestDay(yymmdd)
            newDigestDay.garminConnectSynced = true
            digestDayMap[yymmdd] = newDigestDay
          }
        })
      }

      if (newParticipantDigest.dexcomDataDates) {
        newParticipantDigest.dexcomDataDates.forEach((yymmdd) => {
          if (digestDayMap[yymmdd]) {
            digestDayMap[yymmdd].dexcomSynced = true
          } else {
            const newDigestDay = getEmptyDigestDay(yymmdd)
            newDigestDay.dexcomSynced = true
            digestDayMap[yymmdd] = newDigestDay
          }
        })
      }
    }

    if (participantDigest || newParticipantDigest) {
      return {
        ...adherence,
        digestDayMap: {
          ...adherence.digestDayMap,
          ...digestDayMap,
        },
      }
    }

    return adherence
  })
}

function getEmptyDigestDay(yymmddIndex: number): DigestDay {
  const yymmddDate = parseYYMMDDIndexToUtcDate(yymmddIndex)
  return {
    yymmddIndex: yymmddIndex,
    day: convertYYMMDDIndexToString(yymmddIndex),
    dayDate: yymmddDate.toISOString(),
    dayUnixTimestamp: yymmddDate.getTime(),
  }
}

function convertGarminDataTypeToAdherenceKey(dataTypeString: string): GarminAdherenceKey | undefined {
  const dataType = dataTypeString as GarminDeviceLogDataType
  switch (dataType) {
    case GarminDeviceLogDataType.GarminBBI:
      return 'bbi'
    case GarminDeviceLogDataType.GarminEnhancedBBI:
      return 'enhancedBbi'
    case GarminDeviceLogDataType.GarminStep:
      return 'steps'
    case GarminDeviceLogDataType.GarminPulseOx:
      return 'pulseOx'
    case GarminDeviceLogDataType.GarminHeartRate:
      return 'heartRate'
    case GarminDeviceLogDataType.GarminRespiration:
      return 'respiration'
    case GarminDeviceLogDataType.GarminStress:
      return 'stress'
    case GarminDeviceLogDataType.GarminZeroCrossing:
      return 'zeroCrossing'
    case GarminDeviceLogDataType.GarminActigraphy:
      return 'actigraphy'
    case GarminDeviceLogDataType.GarminActigraphy2:
      return 'actigraphy'
    case GarminDeviceLogDataType.GarminActigraphy3:
      return 'actigraphy'
    case GarminDeviceLogDataType.GarminAcc:
      return 'acc'
    case GarminDeviceLogDataType.GarminTemperature:
      return 'temperature'
    default:
      return
  }
}

function parseYYMMDDIndexToUtcDate(yymmddIndex: number): Date {
  const year = 2000 + Math.trunc(yymmddIndex / 10000)
  const month = Math.trunc((yymmddIndex % 10000) / 100)
  const day = Math.trunc(yymmddIndex % 100)

  return new Date(Date.UTC(year, month - 1, day))
}

function convertYYMMDDIndexToString(yymmddIndex: number): string {
  const year = 2000 + Math.trunc(yymmddIndex / 10000)
  const month = Math.trunc((yymmddIndex % 10000) / 100)
    .toString()
    .padStart(2, '0')
  const day = Math.trunc(yymmddIndex % 100)
    .toString()
    .padStart(2, '0')
  return `${year}-${month}-${day}`
}
